/**
 * Feito pelo agente netexperts
 * Módulo: AnalyticsObra — hook de leitura lazy por tab (com AbortController)
 * Data: 2026-06-14
 *
 * Estratégia de memória: busca SOMENTE a área ativa, cancela requests anteriores ao
 * trocar de tab/filtro, e nunca acumula páginas no estado. Os agregados retornados são
 * leves (KPIs + séries + topN). O drill-down é paginado via fetchDetalhes.
 */
import { useContext, useEffect, useRef, useState, useCallback } from 'react'
import { AuthContextApi } from '../../../../Context/api'
import { toIsoDate } from '../../../../utils/obraAnalytics'

const isAbort = (err) => err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError'

const normalizeDatasExibicao = (items) =>
    (Array.isArray(items) ? items : []).map((item) => ({
        ...item,
        data_inicial: toIsoDate(item.data_inicial),
        data_final: toIsoDate(item.data_final),
    }))

const buildPeriodParams = (params = {}, requirePeriod = false) => {
    const inicio = toIsoDate(params.inicio)
    const fim = toIsoDate(params.fim)
    if (requirePeriod && (!inicio || !fim)) return null
    const next = { ...params, inicio, fim }
    if (!inicio) delete next.inicio
    if (!fim) delete next.fim
    return next
}

export const useObraResumo = (obraId, investidor, params, { enabled = true, requirePeriod = false } = {}) => {
    const { getObraAnalyticsResumo } = useContext(AuthContextApi)
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(false)
    const requestParams = buildPeriodParams(params, requirePeriod)
    const paramsKey = JSON.stringify(requestParams)

    useEffect(() => {
        if (!obraId || !enabled) return
        if (requirePeriod && !requestParams) {
            setData(null)
            setLoading(false)
            setError(false)
            return
        }

        const controller = new AbortController()
        let cancelled = false
        setLoading(true)
        setError(false)

        getObraAnalyticsResumo(obraId, requestParams || {}, investidor, controller.signal)
            .then((res) => {
                if (cancelled) return
                setData(res ?? null)
                setLoading(false)
            })
            .catch((err) => {
                if (cancelled || isAbort(err)) return
                setError(true)
                setLoading(false)
            })

        return () => {
            cancelled = true
            controller.abort()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [obraId, investidor, paramsKey, enabled, requirePeriod])

    return { data, loading, error }
}

export const useObraArea = (obraId, area, params, investidor, { enabled = true, requirePeriod = true } = {}) => {
    const { getObraAnalyticsArea } = useContext(AuthContextApi)
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(false)
    const requestParams = buildPeriodParams(params, requirePeriod)
    const paramsKey = JSON.stringify(requestParams)

    useEffect(() => {
        if (!obraId || !area || !enabled) return
        if (requirePeriod && !requestParams) {
            setData(null)
            setLoading(false)
            setError(false)
            return
        }

        const controller = new AbortController()
        let cancelled = false
        setLoading(true)
        setError(false)

        getObraAnalyticsArea(obraId, area, requestParams || {}, investidor, controller.signal)
            .then((res) => {
                if (cancelled) return
                setData(res ?? null)
                setLoading(false)
            })
            .catch((err) => {
                if (cancelled || isAbort(err)) return
                setError(true)
                setLoading(false)
            })

        return () => {
            cancelled = true
            controller.abort()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [obraId, area, paramsKey, investidor, enabled, requirePeriod])

    return { data, loading, error }
}

// Datas de exibição cadastradas (admin) — usadas como portão do painel de obras.
export const useDatasExibicaoAdmin = (enabled) => {
    const { getDataExibicao } = useContext(AuthContextApi)
    const [datas, setDatas] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(false)

    useEffect(() => {
        if (!enabled) return
        const controller = new AbortController()
        setLoading(true)
        setError(false)
        getDataExibicao({ per_page: 200, sort: 'data_inicial', sort_dir: 'desc' }, controller.signal)
            .then((res) => { setDatas(normalizeDatasExibicao(res)); setLoading(false) })
            .catch((err) => { if (!isAbort(err)) { setError(true); setLoading(false) } })
        return () => controller.abort()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled])

    return { datas, loading, error }
}

// Datas de exibição liberadas ao investidor — usadas como portão do painel.
export const useDatasExibicaoInvestidor = (enabled) => {
    const { getDatasExibicaoInvestidor } = useContext(AuthContextApi)
    const [datas, setDatas] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(false)

    useEffect(() => {
        if (!enabled) return
        const controller = new AbortController()
        setLoading(true)
        setError(false)
        getDatasExibicaoInvestidor(controller.signal)
            .then((res) => { setDatas(normalizeDatasExibicao(res)); setLoading(false) })
            .catch((err) => { if (!isAbort(err)) { setError(true); setLoading(false) } })
        return () => controller.abort()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled])

    return { datas, loading, error }
}

// Execuções de busca de dados da obra (admin) — busca sob demanda, paginada via backend.
export const useConfiguracaoObraBusca = (obraId) => {
    const { getConfiguracaoObraBusca } = useContext(AuthContextApi)
    const [rows, setRows] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(false)
    const controllerRef = useRef(null)

    const fetchExecucoes = useCallback(async (params) => {
        if (!obraId) return
        if (controllerRef.current) controllerRef.current.abort()
        const controller = new AbortController()
        controllerRef.current = controller
        setLoading(true)
        setError(false)
        try {
            const res = await getConfiguracaoObraBusca(obraId, params, controller.signal)
            setRows(Array.isArray(res) ? res : [])
            setLoading(false)
        } catch (err) {
            if (!isAbort(err)) { setError(true); setLoading(false) }
        }
    }, [obraId, getConfiguracaoObraBusca])

    useEffect(() => () => { if (controllerRef.current) controllerRef.current.abort() }, [])

    return { rows, loading, error, fetchExecucoes }
}

// Drill-down paginado (carregado sob demanda; não guarda mais que a página visível)
export const useObraDetalhes = (obraId, area, investidor) => {
    const { getObraAnalyticsDetalhes } = useContext(AuthContextApi)
    const [data, setData] = useState({ rows: [], page: 0, total: 0, total_pages: 0 })
    const [loading, setLoading] = useState(false)
    const controllerRef = useRef(null)

    const fetchDetalhes = useCallback(async (params) => {
        if (!obraId || !area) return
        if (controllerRef.current) controllerRef.current.abort()
        const controller = new AbortController()
        controllerRef.current = controller
        setLoading(true)
        try {
            const res = await getObraAnalyticsDetalhes(obraId, area, params, investidor, controller.signal)
            setData(res)
            setLoading(false)
        } catch (err) {
            if (!isAbort(err)) setLoading(false)
        }
    }, [obraId, area, investidor, getObraAnalyticsDetalhes])

    useEffect(() => () => { if (controllerRef.current) controllerRef.current.abort() }, [])

    return { data, loading, fetchDetalhes }
}

// Detalhe de 1 contrato de venda — carregado sob demanda ao expandir linha (AbortController).
export const useVendaContratoExpansao = (obraId, contrato, investidor, { enabled = false, cached, onLoaded } = {}) => {
    const { getObraVendaContrato } = useContext(AuthContextApi)
    const [data, setData] = useState(cached ?? null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(false)

    useEffect(() => {
        if (!enabled || !obraId || !contrato) return
        if (cached) {
            setData(cached)
            setLoading(false)
            setError(false)
            return
        }

        const controller = new AbortController()
        let cancelled = false
        setLoading(true)
        setError(false)

        getObraVendaContrato(obraId, contrato, investidor, controller.signal)
            .then((res) => {
                if (cancelled) return
                setData(res ?? null)
                setLoading(false)
                if (res) onLoaded?.(contrato, res)
            })
            .catch((err) => {
                if (cancelled || isAbort(err)) return
                setError(true)
                setLoading(false)
            })

        return () => {
            cancelled = true
            controller.abort()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [obraId, contrato, investidor, enabled, cached])

    return { data, loading, error }
}

// Detalhe de 1 unidade de estoque — lazy load ao expandir (com vínculo venda/contrato).
export const useEstoqueUnidadeExpansao = (obraId, params, investidor, { enabled = false, cached, onLoaded } = {}) => {
    const { getObraEstoqueUnidade } = useContext(AuthContextApi)
    const [data, setData] = useState(cached ?? null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(false)
    const paramsKey = JSON.stringify(params)

    useEffect(() => {
        if (!enabled || !obraId || (!params?.id && !params?.identificador)) return
        if (cached) {
            setData(cached)
            setLoading(false)
            setError(false)
            return
        }

        const controller = new AbortController()
        let cancelled = false
        setLoading(true)
        setError(false)

        getObraEstoqueUnidade(obraId, params, investidor, controller.signal)
            .then((res) => {
                if (cancelled) return
                setData(res ?? null)
                setLoading(false)
                if (res) onLoaded?.(paramsKey, res)
            })
            .catch((err) => {
                if (cancelled || isAbort(err)) return
                setError(true)
                setLoading(false)
            })

        return () => {
            cancelled = true
            controller.abort()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [obraId, investidor, enabled, paramsKey, cached])

    return { data, loading, error }
}
